import cn from 'classnames';
import React, { useState } from 'react';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import ImageComponent from '../Image/Image';
import type { GalleryImage } from '../ImageGalleryPopup';
import { ImageGalleryPopup } from '../ImageGalleryPopup';
import { ZoomImageButton } from '../ZoomImageButton';
import styles from './ImageWithPreview.module.css';

export type ImageWithPreviewProps<T extends GalleryImage> = {
  className?: string;
  background?: string;
  height?: number | string;
  blur?: boolean;
  withZoomIcon?: boolean;
  onImageVisible?: (image: T) => void;
} & ({ image: T } | { images: T[]; imageIndex: number });

export const ImageWithPreview = <T extends GalleryImage>({
  className,
  background,
  height,
  blur,
  withZoomIcon,
  onImageVisible,
  ...props
}: ImageWithPreviewProps<T>) => {
  const image = 'image' in props ? props.image : props.images[props.imageIndex];
  const images = 'image' in props ? [props.image] : props.images;

  const [isOpened, setOpened] = useState(false);

  const handleOpenGallery = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    setOpened(true);
  };

  const handleCloseGallery = () => {
    setOpened(false);
  };

  const handlePopupClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const blurBackground = !!blur && !!image && !background;

  const isSVG = !!image?.src && image.src.endsWith('.svg');
  const isPhone = useBreakpoint('phone');

  return (
    <div className={cn(styles.imageWrapper, className)}>
      {withZoomIcon && <div className={styles.overlay} />}

      {blurBackground && (
        <div
          className={styles.imageWrapperInset}
          style={{ background: `url('${image.src}') center center/cover no-repeat` }}
        />
      )}

      {!!image && (
        <ImageComponent
          className={cn(styles.image, {
            [c(styles.fixedHeight)]: !!height,
            [c(styles.withZoomIcon)]: withZoomIcon,
            [c(styles.svgImage)]: isSVG,
          })}
          image={image}
          style={{
            height,
            backgroundColor: blurBackground ? undefined : background,
          }}
          {...(!withZoomIcon && !isPhone && { onClick: handleOpenGallery })}
          {...(onImageVisible && {
            onImageVisible: () => {
              onImageVisible(image);
            },
          })}
        />
      )}

      {withZoomIcon && !isPhone && <ZoomImageButton className={styles.imagePreviewIcon} onClick={handleOpenGallery} />}

      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div onClick={handlePopupClick}>
        <ImageGalleryPopup
          opened={isOpened}
          onClose={handleCloseGallery}
          images={images}
          startImageIndex={'imageIndex' in props ? props.imageIndex : 0}
          onImageVisible={onImageVisible}
        />
      </div>
    </div>
  );
};
